import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import { SearchDropdown } from './SearchDropdown';
import searchIcon from '../../assets/icon-header/Search.svg';
import searchIconSimple from '../../assets/search-icon.svg';
import styles from './SearchInput.module.css';

interface SearchInputProps {
  onClose?: () => void;
}

export const SearchInput: React.FC<SearchInputProps> = ({ onClose }) => {
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Tập trung vào input khi mở rộng
  useEffect(() => {
    if (expanded && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [expanded]);

  // Đóng khi click bên ngoài hoặc nhấn ESC
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      if (!containerRef.current) return;
      
      // Kiểm tra xem click có phải trên tabs hoặc bên trong dropdown không
      const isClickOnTabs = target.closest('[role="tab"]') !== null || 
                           target.closest('.ant-tabs-tab') !== null ||
                           target.closest('.ant-tabs-nav') !== null ||
                           target.closest('.ant-tabs') !== null;
      
      // Kiểm tra xem click có phải bên trong container tìm kiếm (bao gồm dropdown) không
      const isClickInContainer = containerRef.current.contains(target);
      
      // Chỉ đóng nếu click bên ngoài container VÀ không phải trên tabs
      if (!isClickInContainer && !isClickOnTabs) {
        handleClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && expanded) {
        handleClose();
      }
    };

    if (expanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [expanded]);

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleClose = () => {
    setExpanded(false);
    setSearchQuery('');
    setShowResults(false);
    onClose?.();
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowResults(value.trim().length > 0);
  };

  const handleResultSelect = () => {
    handleClose();
  };

  return (
    <div ref={containerRef} className={`${styles.searchContainer} ${expanded ? styles.expanded : ''}`}>
      {!expanded && (
        <button
          type="button"
          className={styles.searchIconButton}
          onClick={handleExpand}
          aria-label="Tìm kiếm"
        >
          <img src={searchIcon} alt="Search" className={styles.searchIcon} />
        </button>
      )}
      {expanded && (
        <div className={styles.searchInputWrapper}>
          <Input
            ref={inputRef}
            placeholder="Tìm kiếm nhân sự, đơn từ,..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onPressEnter={() => {}}
            className={styles.searchInput}
            prefix={<img src={searchIconSimple} alt="Search" className={styles.searchIconPrefix} />}
            allowClear={false}
            autoFocus
          />
          {showResults && (
            <SearchDropdown
              searchQuery={searchQuery}
              onSelect={handleResultSelect}
            />
          )}
        </div>
      )}
    </div>
  );
};
